import { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ShoppingCart, Search, User, X, Star, MessageSquare, Heart } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useCustomerAuth } from '../context/CustomerAuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Logo } from '../components/Logo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MenuPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeSubcategory, setActiveSubcategory] = useState('all');
  const { addItem, itemCount } = useCart();
  const { isAuthenticated, customer, token: customerToken } = useCustomerAuth();
  const [favourites, setFavourites] = useState([]);
  
  const activeCategory = searchParams.get('category') || 'all';

  useEffect(() => {
    document.title = 'Menu - SizzleBox';
    const fetchData = async () => {
      try {
        const [catRes, subRes, menuRes, settingsRes] = await Promise.all([
          axios.get(`${API}/categories`),
          axios.get(`${API}/subcategories`),
          axios.get(`${API}/menu`),
          axios.get(`${API}/settings`)
        ]);
        setCategories(catRes.data);
        setSubcategories(subRes.data);
        setMenuItems(menuRes.data);
        setSettings(settingsRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Load favourites for logged-in customers
  useEffect(() => {
    if (!isAuthenticated || !customerToken) return;
    axios.get(`${API}/customer/favourites`, { headers: { Authorization: `Bearer ${customerToken}` } })
      .then(res => setFavourites(res.data.favourites || []))
      .catch(() => {});
  }, [isAuthenticated, customerToken]);

  const toggleFavourite = async (itemId) => {
    if (!isAuthenticated || !customerToken) return;
    try {
      const res = await axios.post(`${API}/customer/favourites/${itemId}`, {}, { headers: { Authorization: `Bearer ${customerToken}` } });
      setFavourites(res.data.favourites || []);
    } catch (_) {}
  };

  const handleAddToCart = (item) => {
    addItem({
      menu_item_id: item.id,
      name: item.name,
      price: item.price,
      image_url: item.image_url
    });
  };

  // Reset subcategory when category changes
  useEffect(() => {
    setActiveSubcategory('all');
  }, [activeCategory]);

  // Filter items by search and category
  const filteredItems = menuItems.filter(item => {
    const matchesCategory = activeCategory === 'all' || 
      categories.find(c => c.slug === activeCategory)?.id === item.category_id;
    const matchesSearch = !searchQuery || 
      item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesSubcategory = activeSubcategory === 'all' || item.subcategory_id === activeSubcategory;
    return matchesCategory && matchesSearch && matchesSubcategory;
  });

  // Get subcategories for the active category
  const activeCategoryId = activeCategory === 'all' 
    ? null 
    : categories.find(c => c.slug === activeCategory)?.id;

  const categorySubcategories = activeCategoryId
    ? subcategories.filter(s => s.category_id === activeCategoryId)
    : [];

  // Ref for scrolling to subcategory sections
  const sectionRefs = useRef({});

  // Get subcategories for the active category (or all if "all" selected)
  const relevantSubcategories = activeCategoryId
    ? subcategories.filter(s => s.category_id === activeCategoryId)
    : subcategories;

  // Group items by subcategory
  const getItemsForSubcategory = (subId) => 
    filteredItems.filter(item => item.subcategory_id === subId);

  // Items without a subcategory
  const uncategorizedItems = filteredItems.filter(item => !item.subcategory_id);

  // Check if we're in search mode (flat view)
  const isSearching = searchQuery.length > 0;

  // Build sections: subcategories that have items, grouped by parent category
  const buildSections = () => {
    if (isSearching) return []; // flat view for search

    const sections = [];

    if (activeCategory === 'all') {
      // Group by category, then subcategory within
      for (const cat of categories) {
        const catSubs = subcategories.filter(s => s.category_id === cat.id);
        const catItems = filteredItems.filter(i => i.category_id === cat.id);
        if (catItems.length === 0) continue;

        const subSections = [];
        for (const sub of catSubs) {
          const subItems = catItems.filter(i => i.subcategory_id === sub.id);
          if (subItems.length > 0) {
            subSections.push({ subcategory: sub, items: subItems });
          }
        }
        // Items with no subcategory in this category
        const noSubItems = catItems.filter(i => !i.subcategory_id);
        if (noSubItems.length > 0) {
          subSections.push({ subcategory: null, items: noSubItems });
        }

        if (subSections.length > 0) {
          sections.push({ category: cat, subSections });
        }
      }
    } else {
      // Single category view
      const cat = categories.find(c => c.slug === activeCategory);
      if (!cat) return sections;

      const catSubs = subcategories.filter(s => s.category_id === cat.id);
      const subSections = [];
      for (const sub of catSubs) {
        const subItems = filteredItems.filter(i => i.subcategory_id === sub.id);
        if (subItems.length > 0) {
          subSections.push({ subcategory: sub, items: subItems });
        }
      }
      const noSubItems = filteredItems.filter(i => !i.subcategory_id);
      if (noSubItems.length > 0) {
        subSections.push({ subcategory: null, items: noSubItems });
      }
      if (subSections.length > 0) {
        sections.push({ category: cat, subSections });
      }
    }

    return sections;
  };

  const sections = buildSections();

  const [reviewModal, setReviewModal] = useState(null);
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewComment, setReviewComment] = useState('');
  const [reviewName, setReviewName] = useState('');
  const [submittingReview, setSubmittingReview] = useState(false);
  const [itemReviews, setItemReviews] = useState([]);

  const openReviewModal = async (item) => {
    setReviewModal(item);
    setReviewRating(5);
    setReviewComment('');
    setReviewName(customer?.name || '');
    try {
      const res = await axios.get(`${API}/reviews/${item.id}`);
      setItemReviews(res.data);
    } catch (_) { setItemReviews([]); }
  };

  const submitReview = async () => {
    if (!reviewModal) return;
    setSubmittingReview(true);
    try {
      await axios.post(`${API}/reviews`, {
        menu_item_id: reviewModal.id,
        rating: reviewRating,
        comment: reviewComment || null,
        customer_name: reviewName || 'Anonymous',
      });
      // Refresh menu items to get updated avg_rating
      const menuRes = await axios.get(`${API}/menu`);
      setMenuItems(menuRes.data);
      // Refresh reviews
      const revRes = await axios.get(`${API}/reviews/${reviewModal.id}`);
      setItemReviews(revRes.data);
      setReviewComment('');
      setReviewRating(5);
    } catch (e) {
      console.error('Review error:', e);
    }
    setSubmittingReview(false);
  };

  const StarRating = ({ rating, size = 'w-4 h-4', interactive = false, onRate }) => (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map(star => (
        <Star
          key={star}
          className={`${size} ${star <= rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-600'} ${interactive ? 'cursor-pointer hover:scale-110 transition-transform' : ''}`}
          onClick={interactive ? () => onRate(star) : undefined}
        />
      ))}
    </div>
  );

  const renderItemCard = (item) => (
    <div
      key={item.id}
      className="group bg-card rounded-2xl overflow-hidden border border-white/10 hover:border-fire-500/50 transition-all card-hover"
      data-testid={`menu-page-item-${item.id}`}
    >
      {item.image_url ? (
        <div className="relative h-48 overflow-hidden">
          <img
            src={item.image_url}
            alt={item.name}
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
            loading="lazy"
          />
          {item.tags?.includes('bestseller') && (
            <span className="absolute top-4 left-4 bg-fire-500 text-white text-xs font-bold px-3 py-1 rounded-full animate-pulse-fire">
              Bestseller
            </span>
          )}
          {isAuthenticated && (
            <button
              onClick={(e) => { e.stopPropagation(); toggleFavourite(item.id); }}
              className="absolute top-4 right-4 p-2 rounded-full bg-black/50 hover:bg-black/70 transition-colors"
              data-testid={`fav-btn-${item.id}`}
            >
              <Heart className={`w-5 h-5 transition-colors ${favourites.includes(item.id) ? 'text-red-500 fill-red-500' : 'text-white'}`} />
            </button>
          )}
        </div>
      ) : (
        <div className="relative h-32 bg-gradient-to-br from-fire-500/10 to-transparent flex items-center justify-center">
          <span className="text-4xl opacity-60">🍽️</span>
          {item.tags?.includes('bestseller') && (
            <span className="absolute top-4 left-4 bg-fire-500 text-white text-xs font-bold px-3 py-1 rounded-full">
              Bestseller
            </span>
          )}
        </div>
      )}
      <div className="p-4 sm:p-6">
        <div className="flex items-start justify-between mb-1">
          <h3 className="text-white font-bold text-base sm:text-lg">{item.name}</h3>
          {(item.avg_rating > 0) && (
            <div className="flex items-center gap-1 flex-shrink-0">
              <Star className="w-3.5 h-3.5 text-yellow-400 fill-yellow-400" />
              <span className="text-yellow-400 text-sm font-semibold">{item.avg_rating}</span>
              <span className="text-gray-500 text-xs">({item.review_count})</span>
            </div>
          )}
        </div>
        <p className="text-gray-400 text-sm mb-3 line-clamp-2">{item.description}</p>
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
          <div className="flex items-center gap-3">
            <span className="text-fire-500 font-bold text-lg sm:text-xl">₦{item.price.toLocaleString()}</span>
            <button
              onClick={() => openReviewModal(item)}
              className="text-gray-500 hover:text-yellow-400 transition-colors flex items-center gap-1 text-xs"
              data-testid={`review-btn-${item.id}`}
            >
              <MessageSquare className="w-3.5 h-3.5" />Reviews
            </button>
          </div>
          <div className="flex gap-2 w-full sm:w-auto">
            <a
              href={`https://wa.me/${(settings?.whatsapp?.replace(/[^0-9]/g, '') || '').replace(/^0/, '234')}?text=Hi! I'd like to order ${item.name} - ₦${item.price.toLocaleString()}`}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-[#25D366] hover:bg-[#128C7E] text-white px-3 sm:px-4 py-2 rounded-full text-sm font-semibold transition-colors flex-1 sm:flex-none text-center"
            >
              WhatsApp
            </a>
            <Button
              onClick={() => handleAddToCart(item)}
              className="bg-fire-500 hover:bg-fire-600 text-white px-3 sm:px-4 py-2 rounded-full text-sm font-semibold flex-1 sm:flex-none"
              data-testid={`menu-add-to-cart-${item.id}`}
            >
              Add to Cart
            </Button>
          </div>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-fire-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black" data-testid="menu-page">
      {/* Navbar */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-black/80 backdrop-blur-md border-b border-white/10">
        <div className="container mx-auto px-6">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex items-center">
              <Logo className="h-12" />
            </Link>
            <div className="hidden md:flex items-center gap-8">
              <Link to="/" className="text-white hover:text-fire-500 transition-colors">Home</Link>
              <Link to="/menu" className="text-fire-500">Menu</Link>
            </div>
            <div className="flex items-center gap-3">
              <Link to="/cart" className="relative" data-testid="menu-cart-icon">
                <ShoppingCart className="w-6 h-6 text-white" />
                {itemCount > 0 && (
                  <span className="absolute -top-2 -right-2 bg-fire-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                    {itemCount}
                  </span>
                )}
              </Link>
              {isAuthenticated ? (
                <Link to="/account" data-testid="menu-account-btn">
                  <Button variant="outline" className="border-white/20 text-white hover:bg-white/10 rounded-full px-4 hidden md:flex items-center gap-2">
                    <User className="w-4 h-4" />
                    {customer?.name?.split(' ')[0] || 'Account'}
                  </Button>
                </Link>
              ) : (
                <Link to="/login" data-testid="menu-login-btn">
                  <Button variant="outline" className="border-white/20 text-white hover:bg-white/10 rounded-full px-4 hidden md:flex items-center gap-2">
                    <User className="w-4 h-4" />
                    Sign In
                  </Button>
                </Link>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Content */}
      <div className="pt-28 pb-20">
        <div className="container mx-auto px-6">
          {/* Header */}
          <div className="mb-12">
            <h1 className="font-heading text-5xl md:text-6xl text-white mb-4">Our Menu</h1>
            <p className="text-gray-400 text-lg">Fresh. Juicy. Delivered Hot.</p>
          </div>

          {/* Search & Filter */}
          <div className="flex flex-col md:flex-row gap-4 mb-8">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
              <Input
                type="text"
                placeholder="Search for burgers, milkshakes, grills..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-12 pr-24 bg-card border-white/10 text-white h-12 rounded-full"
                data-testid="menu-search-input"
              />
              {searchQuery && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-2">
                  <span className="text-xs text-gray-500">{filteredItems.length} found</span>
                  <button
                    onClick={() => setSearchQuery('')}
                    className="text-gray-400 hover:text-white p-1"
                    data-testid="menu-search-clear"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Category Tabs */}
          <div className="flex flex-wrap gap-3 mb-6">
            <Button
              variant={activeCategory === 'all' ? 'default' : 'outline'}
              className={`rounded-full ${activeCategory === 'all' ? 'bg-fire-500 hover:bg-fire-600' : 'border-white/20 text-white hover:border-fire-500'}`}
              onClick={() => setSearchParams({})}
              data-testid="category-all"
            >
              All Items
            </Button>
            {categories.map(cat => (
              <Button
                key={cat.id}
                variant={activeCategory === cat.slug ? 'default' : 'outline'}
                className={`rounded-full ${activeCategory === cat.slug ? 'bg-fire-500 hover:bg-fire-600' : 'border-white/20 text-white hover:border-fire-500'}`}
                onClick={() => setSearchParams({ category: cat.slug })}
                data-testid={`category-tab-${cat.slug}`}
              >
                {cat.name}
              </Button>
            ))}
          </div>

          {/* Subcategory Pills (when a category is selected) */}
          {activeCategory !== 'all' && categorySubcategories.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-10" data-testid="subcategory-tabs">
              <button
                onClick={() => setActiveSubcategory('all')}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${
                  activeSubcategory === 'all'
                    ? 'bg-white text-black'
                    : 'bg-white/10 text-gray-300 hover:bg-white/20'
                }`}
                data-testid="subcategory-all"
              >
                All {categories.find(c => c.slug === activeCategory)?.name}
              </button>
              {categorySubcategories.map(sub => (
                <button
                  key={sub.id}
                  onClick={() => {
                    setActiveSubcategory(sub.id);
                    // Scroll to section if in "all subcategories" view
                    if (activeSubcategory === 'all' && sectionRefs.current[sub.id]) {
                      sectionRefs.current[sub.id].scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                  }}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${
                    activeSubcategory === sub.id
                      ? 'bg-white text-black'
                      : 'bg-white/10 text-gray-300 hover:bg-white/20'
                  }`}
                  data-testid={`subcategory-tab-${sub.id}`}
                >
                  {sub.name}
                </button>
              ))}
            </div>
          )}

          {/* Menu Content */}
          {isSearching ? (
            /* Flat grid when searching */
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {filteredItems.map(item => renderItemCard(item))}
              </div>
              {filteredItems.length === 0 && (
                <div className="text-center py-20">
                  <p className="text-gray-400 text-xl">No items found for "{searchQuery}"</p>
                </div>
              )}
            </>
          ) : (
            /* Grouped by category and subcategory */
            <div className="space-y-16">
              {sections.map(section => (
                <div key={section.category.id} data-testid={`category-section-${section.category.slug}`}>
                  {/* Category heading (only in "All" view) */}
                  {activeCategory === 'all' && (
                    <div className="mb-8 flex items-center gap-4">
                      <h2 className="font-heading text-3xl md:text-4xl text-white">{section.category.name}</h2>
                      <div className="flex-1 h-px bg-white/10" />
                      <Button
                        variant="outline"
                        className="border-white/20 text-gray-400 hover:text-white hover:border-fire-500 rounded-full text-sm"
                        onClick={() => setSearchParams({ category: section.category.slug })}
                      >
                        View All
                      </Button>
                    </div>
                  )}

                  {/* Subcategory sections */}
                  <div className="space-y-12">
                    {section.subSections.map((subSection, idx) => (
                      <div
                        key={subSection.subcategory?.id || `uncategorized-${idx}`}
                        ref={el => { if (subSection.subcategory) sectionRefs.current[subSection.subcategory.id] = el; }}
                        data-testid={`subcategory-section-${subSection.subcategory?.id || 'other'}`}
                        style={{ scrollMarginTop: '120px' }}
                      >
                        {subSection.subcategory && (
                          <div className="mb-6">
                            <h3 className="text-xl md:text-2xl font-bold text-white">{subSection.subcategory.name}</h3>
                            {subSection.subcategory.description && (
                              <p className="text-gray-500 text-sm mt-1">{subSection.subcategory.description}</p>
                            )}
                          </div>
                        )}
                        {!subSection.subcategory && section.subSections.length > 1 && (
                          <div className="mb-6">
                            <h3 className="text-xl md:text-2xl font-bold text-white">Other</h3>
                          </div>
                        )}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                          {subSection.items.map(item => renderItemCard(item))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              {sections.length === 0 && filteredItems.length === 0 && (
                <div className="text-center py-20">
                  <p className="text-gray-400 text-xl">No items found</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Floating Cart Button */}
      {itemCount > 0 && (
        <Link
          to="/cart"
          className="fixed bottom-6 right-6 bg-fire-500 text-white px-6 py-4 rounded-full shadow-lg flex items-center gap-3 hover:bg-fire-600 transition-all"
          data-testid="floating-cart-btn"
        >
          <ShoppingCart className="w-5 h-5" />
          <span className="font-bold">View Cart ({itemCount})</span>
        </Link>
      )}

      {/* Review Modal */}
      {reviewModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={() => setReviewModal(null)}>
          <div className="bg-[#1A1A1A] border border-white/10 rounded-2xl w-full max-w-md max-h-[85vh] overflow-hidden" onClick={e => e.stopPropagation()} data-testid="review-modal">
            <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between">
              <h3 className="text-white font-bold text-lg">Reviews - {reviewModal.name}</h3>
              <button onClick={() => setReviewModal(null)} className="text-gray-400 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            
            {/* Write Review */}
            <div className="px-5 py-4 border-b border-white/10 space-y-3">
              <p className="text-gray-400 text-sm font-semibold">Leave a review</p>
              <StarRating rating={reviewRating} size="w-6 h-6" interactive onRate={setReviewRating} />
              <Input
                placeholder="Your name"
                value={reviewName}
                onChange={e => setReviewName(e.target.value)}
                className="bg-black/50 border-white/10 text-white text-sm"
                data-testid="review-name-input"
              />
              <textarea
                placeholder="Share your experience..."
                value={reviewComment}
                onChange={e => setReviewComment(e.target.value)}
                className="w-full bg-black/50 border border-white/10 text-white text-sm rounded-lg p-3 resize-none h-20 focus:outline-none focus:border-fire-500"
                data-testid="review-comment-input"
              />
              <Button
                onClick={submitReview}
                disabled={submittingReview}
                className="bg-fire-500 hover:bg-fire-600 text-white w-full"
                data-testid="submit-review-btn"
              >
                {submittingReview ? 'Submitting...' : 'Submit Review'}
              </Button>
            </div>

            {/* Reviews List */}
            <div className="px-5 py-3 overflow-y-auto max-h-60 space-y-3">
              {itemReviews.length === 0 ? (
                <p className="text-gray-500 text-sm text-center py-4">No reviews yet. Be the first!</p>
              ) : itemReviews.map(rev => (
                <div key={rev.id} className="bg-black/30 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-white text-sm font-semibold">{rev.customer_name || 'Anonymous'}</span>
                    <StarRating rating={rev.rating} size="w-3 h-3" />
                  </div>
                  {rev.comment && <p className="text-gray-400 text-sm">{rev.comment}</p>}
                  <p className="text-gray-600 text-xs mt-1">{new Date(rev.created_at).toLocaleDateString()}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
